import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  api,
  APIError,
  EMPTY_STRATEGY,
  isFeasibilityDetail,
  isQuotaError,
  strategyFromMetadata,
  type AT,
  type ContentLang,
  type Course,
  type CourseFormatEntry,
  type CourseSession,
  type FeasibilityReport,
  type ILO,
  type SessionKind,
  type StrategyPreset,
  type StrategyValue,
  type TLA,
  type TLAPhase,
  type TranslateItem,
  type WizardCatalog,
} from "@/api/client";
import AiAssistButton from "@/components/AiAssistButton";
import AlignmentOverview, { type OverviewRow } from "@/components/AlignmentOverview";
import ContentLangBanner from "@/components/ContentLangBanner";
import CourseFormatPicker from "@/components/CourseFormatPicker";
import FeasibilityPanel from "@/components/FeasibilityPanel";
import Header from "@/components/Layout/Header";
import HintsPanel from "@/components/HintsPanel";
import LinkedILOsEditor from "@/components/LinkedILOsEditor";
import LoadingOverlay from "@/components/Loading";
import QuotaExhaustedCard from "@/components/QuotaExhaustedCard";
import StepIndicator from "@/components/StepIndicator";
import StrategyPanel from "@/components/StrategyPanel";
import { contextKindLabel } from "@/lib/contexts";
import { computeAlignment, computeHints } from "@/lib/hints";
import {
  useAtFormatLabels,
  useDateLocale,
  useModalityLabels,
  usePhaseOptions,
  useSessionKindLabels,
} from "@/lib/labels";

// Kuratierte Standard-Formate (alle Kontexte, ohne „Anderes") vor den Alt-Katalog
// mischen, damit die neuen Format-IDs auch im Editor auflösbar bleiben. Dedup nach ID.
function mergeEditorFormats(catalog: WizardCatalog): CourseFormatEntry[] {
  const curated = Object.values(catalog.context_formats ?? {})
    .flat()
    .filter((f) => f.id !== "anderes")
    .map((f) => ({ ...f, category: "kuratiert", category_label: "Standard-Formate" }));
  const seen = new Set(curated.map((f) => f.id));
  return [...curated, ...catalog.course_formats.filter((f) => !seen.has(f.id))];
}

const MODALITY_VALUES = ["presence", "synchronous", "asynchronous", "blended"] as const;

// Bender-Tiefenstufen sind Domain-Werte (Backend-Enum, deutsch) — nicht übersetzt.
const BENDER_OPTIONS = ["Wissen", "Verstehen", "Können", "Performanz"] as const;

// AT-Typen: UI-seitige Labels für die hart verdrahtete Auswahl im ATCard
// (unabhängig vom Backend-Katalog at_types) → i18n unter labels.atType.*
const AT_TYPE_VALUES = [
  "mc_test",
  "short_answer",
  "essay",
  "case_analysis",
  "project",
  "portfolio",
  "oral_exam",
  "peer_review",
  "presentation",
] as const;

const AT_TYPE_LABEL_KEY: Record<(typeof AT_TYPE_VALUES)[number], string> = {
  mc_test: "labels.atType.mc_test",
  short_answer: "labels.atType.short_answer",
  essay: "labels.atType.essay",
  case_analysis: "labels.atType.case_analysis",
  project: "labels.atType.project",
  portfolio: "labels.atType.portfolio",
  oral_exam: "labels.atType.oral_exam",
  peer_review: "labels.atType.peer_review",
  presentation: "labels.atType.presentation",
};

function formatSessionDate(dateStr: string | null | undefined, locale: string): string {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

// Konstruktions-Download (vor Audit), Auth via Bearer-Token aus localStorage.
async function downloadConstruction(
  courseId: string,
  format: "pdf" | "md",
  courseTitle: string,
): Promise<void> {
  const url =
    format === "pdf"
      ? api.exportConstructionPdfUrl(courseId)
      : api.exportConstructionMdUrl(courseId);
  const token = localStorage.getItem("ca_access_token");
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error(String(res.status));
  }
  const blob = await res.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const slug = (courseTitle || "konstruktion").replace(/[^a-zA-Z0-9]/g, "_").slice(0, 50);
  a.download = `${slug}_konstruktion.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objectUrl);
}

// Custom hook: debounced auto-save für Card-Eingaben
function useDebouncedSave<T>(
  value: T,
  onSave: (val: T) => void | Promise<void>,
  delay = 600,
) {
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      onSave(value);
    }, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
}

export default function CourseEditor() {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const modalityLabels = useModalityLabels();
  const phaseOptions = usePhaseOptions();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const stepFromUrl = parseInt(searchParams.get("step") || "1", 10);
  const step = stepFromUrl >= 1 && stepFromUrl <= 4 ? stepFromUrl : 1;
  const setStep = (n: number) => setSearchParams({ step: String(n) });

  const [course, setCourse] = useState<Course | null>(null);
  const [catalog, setCatalog] = useState<WizardCatalog | null>(null);
  const [feasibility, setFeasibility] = useState<FeasibilityReport | null>(null);
  const [sessions, setSessions] = useState<CourseSession[]>([]);
  // Aktiver Session-Filter im Methodik-Tab (null = alle anzeigen)
  const [sessionFilter, setSessionFilter] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyMsg, setBusyMsg] = useState("");
  const [autoExportPdf, setAutoExportPdf] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState<"pdf" | "md" | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Lernstrategie (C1/C2): kompletter Strategy-Key aus course.metadata,
  // debounced als Ganzes per PATCH gespeichert (flacher Merge ersetzt den Key).
  const [strategy, setStrategy] = useState<StrategyValue>(EMPTY_STRATEGY);
  const [strategyPresets, setStrategyPresets] = useState<StrategyPreset[]>([]);
  const strategyDirty = useRef(false);
  // Token, um den Übersetzungs-Review-Flow (TranslateBar, Tab „Veranstaltung")
  // von außen zu starten — per Banner-Klick oder Deep-Link ?translate=1.
  const [translateToken, setTranslateToken] = useState(0);

  const STEPS = [
    { num: 1, title: t("editor.steps.1.title"), desc: t("editor.steps.1.desc") },
    { num: 2, title: t("editor.steps.2.title"), desc: t("editor.steps.2.desc") },
    { num: 3, title: t("editor.steps.3.title"), desc: t("editor.steps.3.desc") },
    { num: 4, title: t("editor.steps.4.title"), desc: t("editor.steps.4.desc") },
  ];

  const handleDownload = async (format: "pdf" | "md") => {
    if (!course) return;
    setDownloadBusy(format);
    setDownloadError(null);
    try {
      await downloadConstruction(course.id, format, course.title);
    } catch (e) {
      setDownloadError(
        e instanceof Error && /^\d+$/.test(e.message)
          ? t("common.downloadFailedHttp", { status: e.message })
          : t("common.downloadFailed"),
      );
    } finally {
      setDownloadBusy(null);
    }
  };

  useEffect(() => {
    if (!id) return;
    api.getCourse(id).then((c) => {
      setCourse(c);
      setStrategy(strategyFromMetadata(c.metadata));
    });
    api.wizardCatalog().then(setCatalog).catch(() => setCatalog(null));
    api.listSessions(id).then(setSessions).catch(() => setSessions([]));
    api.strategyPresets().then(setStrategyPresets).catch(() => setStrategyPresets([]));
  }, [id]);

  // Strategie debounced speichern — nur nach echter User-Änderung
  // (strategyDirty), nicht beim initialen Laden aus dem Kurs.
  useEffect(() => {
    if (!strategyDirty.current || !id) return;
    const timer = setTimeout(() => {
      strategyDirty.current = false;
      api
        .updateCourse(id, { metadata: { strategy } })
        .then(() => {
          setCourse((prev) =>
            prev
              ? { ...prev, metadata: { ...(prev.metadata || {}), strategy } }
              : prev,
          );
        })
        .catch(() => {});
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategy, id]);

  // Vollständigkeits-Status jeweils frisch holen, wenn der User zum Audit-Schritt geht
  // (deckt Edits ab, ohne bei jedem Auto-Save die API zu rufen).
  useEffect(() => {
    if (!id || step !== 4) return;
    api.auditFeasibility(id).then(setFeasibility).catch(() => setFeasibility(null));
  }, [id, step]);

  // Deep-Link aus der Kursübersicht (?translate=1): in den Tab „Veranstaltung"
  // springen und dort den Übersetzungs-Review-Flow automatisch starten.
  useEffect(() => {
    if (searchParams.get("translate") !== "1") return;
    setTranslateToken((n) => n + 1);
    setSearchParams({ step: "1" }, { replace: true });
  }, [searchParams, setSearchParams]);

  if (!course) return <LoadingOverlay message={t("overview.loading")} />;

  // Live-Konsistenz-Hinweise („Dialogisches Alignment") — pure Funktion auf
  // dem geladenen course-State, Key = UUID. Aktualisiert sich durch das
  // optimistic patching bei jeder Änderung (max. Debounce-Zeit verzögert).
  const iloList = course.ilos || [];
  const tlaList = course.tlas || [];
  const atList = course.ats || [];
  const hintInput = {
    ilos: iloList,
    tlas: tlaList.map((x) => ({
      type: x.type,
      bloom_levels: x.bloom_levels,
      linked_ilo_keys: x.linked_ilo_ids,
    })),
    ats: atList.map((x) => ({
      type: x.type,
      bloom_levels: x.bloom_levels,
      linked_ilo_keys: x.linked_ilo_ids,
    })),
    iloKey: (i: number) => iloList[i].id,
    tlaKey: (i: number) => tlaList[i].id,
    atKey: (i: number) => atList[i].id,
  };
  const hints = computeHints<string>(hintInput);
  const iloHintLabel = (key: string) =>
    `LZ ${(iloList.find((i) => i.id === key)?.position ?? 0) + 1}`;
  const tlaHintLabel = (key: string) =>
    `${(tlaList.find((x) => x.id === key)?.position ?? 0) + 1}`;
  const atHintLabel = (key: string) =>
    `${(atList.find((x) => x.id === key)?.position ?? 0) + 1}`;
  const overviewRows: OverviewRow[] = computeAlignment<string>(hintInput).map((row) => ({
    iloLabel: iloHintLabel(row.iloKey),
    iloText: iloList[row.iloIndex]?.text || iloList[row.iloIndex]?.action_verb || "",
    tlas: row.tlas.map((p) => ({ label: tlaHintLabel(p.key), severity: p.severity })),
    ats: row.ats.map((p) => ({ label: atHintLabel(p.key), severity: p.severity })),
  }));
  const hintsPanel = (
    <HintsPanel
      hints={hints}
      iloLabel={iloHintLabel}
      tlaLabel={tlaHintLabel}
      atLabel={atHintLabel}
      onGoto={setStep}
    />
  );

  // Optimistic Updates — kein reload nach Aktionen, sondern lokal patchen.
  // Damit gehen User-Eingaben in den Cards nicht durch reload verloren.

  const updateStrategy = (v: StrategyValue) => {
    strategyDirty.current = true;
    setStrategy(v);
  };

  // Kurzinfo der aktiven Strategie für die ✨-Assistenz-Buttons
  // (Preset-Labels + globaler Text + Kategorie-Text).
  const hintFor = (scope: "ilo" | "tla" | "at" | null): string | undefined => {
    const parts: string[] = strategy.presets
      .map((pid) => strategyPresets.find((p) => p.id === pid)?.label)
      .filter((l): l is string => Boolean(l));
    if (strategy.global_text.trim()) parts.push(strategy.global_text.trim());
    const catText =
      scope === "ilo" ? strategy.ilo_text :
      scope === "tla" ? strategy.tla_text :
      scope === "at" ? strategy.at_text : "";
    if (catText.trim()) parts.push(catText.trim());
    if (parts.length === 0) return undefined;
    const joined = parts.join(" · ");
    return joined.length > 160 ? `${joined.slice(0, 160)}…` : joined;
  };

  const updateCourse = async (patch: Partial<Course>) => {
    setCourse((prev) => (prev ? { ...prev, ...patch } : prev));
    await api.updateCourse(course.id, patch);
  };

  // Sprache der Kursinhalte (metadata.content_lang) — flacher Metadata-Merge
  // im Backend erhält die übrigen Keys (Muster wie beim Strategy-Objekt).
  const contentLang: ContentLang =
    (course.metadata?.content_lang as ContentLang | undefined) ?? "de";
  const setContentLang = async (v: ContentLang) => {
    setCourse((prev) =>
      prev ? { ...prev, metadata: { ...(prev.metadata || {}), content_lang: v } } : prev,
    );
    await api.updateCourse(course.id, { metadata: { content_lang: v } });
  };

  const addILO = async () => {
    const newIlo = await api.createILO(course.id, { text: "", action_verb: "" });
    setCourse((prev) => (prev ? { ...prev, ilos: [...(prev.ilos || []), newIlo] } : prev));
  };
  const updateILO = async (iloId: string, patch: Partial<ILO>) => {
    const updated = await api.updateILO(course.id, iloId, patch);
    setCourse((prev) =>
      prev
        ? { ...prev, ilos: (prev.ilos || []).map((i) => (i.id === iloId ? updated : i)) }
        : prev,
    );
  };
  // Hoch/Runter: die beiden benachbarten ILOs tauschen ihre positions.
  // Links sind UUID-basiert und bleiben automatisch korrekt; die Anzeige
  // sortiert nach position, darum genügt der Tausch der beiden Werte.
  const moveILO = async (iloId: string, dir: -1 | 1) => {
    const sorted = [...(course.ilos || [])].sort((a, b) => a.position - b.position);
    const idx = sorted.findIndex((i) => i.id === iloId);
    const other = sorted[idx + dir];
    if (idx < 0 || !other) return;
    const me = sorted[idx];
    await updateILO(me.id, { position: other.position });
    await updateILO(other.id, { position: me.position });
  };
  const deleteILO = async (iloId: string) => {
    await api.deleteILO(course.id, iloId);
    setCourse((prev) =>
      prev ? { ...prev, ilos: (prev.ilos || []).filter((i) => i.id !== iloId) } : prev,
    );
  };

  const addTLA = async () => {
    const newTla = await api.createTLA(course.id, { type: "lecture_classic", description: "" });
    setCourse((prev) => (prev ? { ...prev, tlas: [...(prev.tlas || []), newTla] } : prev));
  };
  const updateTLA = async (tlaId: string, patch: Partial<TLA>) => {
    const updated = await api.updateTLA(course.id, tlaId, patch);
    setCourse((prev) =>
      prev
        ? { ...prev, tlas: (prev.tlas || []).map((x) => (x.id === tlaId ? updated : x)) }
        : prev,
    );
  };
  const deleteTLA = async (tlaId: string) => {
    await api.deleteTLA(course.id, tlaId);
    setCourse((prev) =>
      prev ? { ...prev, tlas: (prev.tlas || []).filter((x) => x.id !== tlaId) } : prev,
    );
  };

  const addAT = async () => {
    const newAt = await api.createAT(course.id, { type: "essay", description: "" });
    setCourse((prev) => (prev ? { ...prev, ats: [...(prev.ats || []), newAt] } : prev));
  };
  const updateAT = async (atId: string, patch: Partial<AT>) => {
    const updated = await api.updateAT(course.id, atId, patch);
    setCourse((prev) =>
      prev
        ? { ...prev, ats: (prev.ats || []).map((a) => (a.id === atId ? updated : a)) }
        : prev,
    );
  };
  const deleteAT = async (atId: string) => {
    await api.deleteAT(course.id, atId);
    setCourse((prev) =>
      prev ? { ...prev, ats: (prev.ats || []).filter((a) => a.id !== atId) } : prev,
    );
  };

  const refreshSessions = async () => {
    const list = await api.listSessions(course.id);
    setSessions(list);
  };

  // Beim Löschen einer Session setzt das Backend TLA/AT.session_id auf NULL —
  // deshalb danach den ganzen Kurs frisch laden.
  const handleSessionDeleted = async () => {
    setSessionFilter(null);
    await refreshSessions();
    setCourse(await api.getCourse(course.id));
  };

  // Einzelnen Übersetzungs-Vorschlag übernehmen — Dispatch auf den passenden
  // bestehenden PATCH-Pfad (kein Blind-Apply, Muster wie der Strategie-Apply
  // in der StrategyPanel).
  const applyTranslateItem = async (item: TranslateItem) => {
    if (item.kind === "course") {
      if (item.field === "title") await updateCourse({ title: item.new_text });
      else if (item.field === "audience") await updateCourse({ audience: item.new_text });
    } else if (item.kind === "ilo") {
      await updateILO(item.id, { text: item.new_text });
    } else if (item.kind === "tla") {
      await updateTLA(item.id, { description: item.new_text });
    } else if (item.kind === "at") {
      await updateAT(item.id, { description: item.new_text });
    } else if (item.kind === "session") {
      const updated = await api.updateSession(course.id, item.id, { title: item.new_text });
      setSessions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    }
  };

  const runAudit = async () => {
    setBusy(true);
    setBusyMsg(t("editor.auditBusy"));
    try {
      const report = await api.runAudit(course.id);
      navigate(`/courses/${course.id}/audit/${report.id}`);
    } catch (err) {
      // 422 = Vollständigkeits-Gate: Mängelliste ins Panel statt alert().
      if (err instanceof APIError && err.status === 422 && isFeasibilityDetail(err.data)) {
        setFeasibility({ feasible: false, blockers: err.data.blockers, warnings: err.data.warnings ?? [] });
      } else {
        setDownloadError(err instanceof APIError ? err.detail : String(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header />
      {busy && <LoadingOverlay message={busyMsg} />}

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Kurs-Header: Die Veranstaltung startet nicht mit den Lernzielen —
            Titel, Zielgruppe und Seminarbeschreibung gehören an den Anfang. */}
        <div className="mb-8">
          <h1 className="text-3xl font-black text-ca-ink tracking-tight">
            {course.title}
          </h1>
          {course.audience && (
            <p className="text-sm text-ca-ink-muted mt-1">
              <span className="font-bold uppercase tracking-widest text-xs mr-2">
                {t("editor.headerAudience")}:
              </span>
              {course.audience}
            </p>
          )}
          {Boolean(course.metadata?.rough_idea) && (
            <p className="text-sm text-ca-ink-muted leading-relaxed mt-3 whitespace-pre-wrap">
              {String(course.metadata?.rough_idea)}
            </p>
          )}
        </div>

        {/* Konstruktions-Download — vor Audit immer verfügbar, damit die Planung nicht verloren geht */}
        <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
          <span className="text-xs font-bold uppercase tracking-wider text-ca-ink-muted mr-2">
            {t("editor.saveConstruction")}
          </span>
          <button
            onClick={() => handleDownload("pdf")}
            disabled={downloadBusy !== null}
            className="text-xs font-semibold px-3 py-1.5 rounded-md border border-ca-surface-border bg-ca-card text-ca-surface-ink hover:border-ca-accent hover:bg-ca-card disabled:opacity-40 transition"
            title={t("editor.downloadPdfTitle")}
          >
            {downloadBusy === "pdf" ? t("common.downloading") : t("overview.downloadPdf")}
          </button>
          <button
            onClick={() => handleDownload("md")}
            disabled={downloadBusy !== null}
            className="text-xs font-semibold px-3 py-1.5 rounded-md border border-ca-surface-border bg-ca-card text-ca-surface-ink hover:border-ca-accent hover:bg-ca-card disabled:opacity-40 transition"
            title={t("editor.downloadMdTitle")}
          >
            {downloadBusy === "md" ? t("common.downloading") : t("editor.downloadMd")}
          </button>
        </div>
        {downloadError && (
          <p className="mb-2 ml-auto w-fit rounded-md border border-ca-surface-border bg-ca-card px-3 py-1.5 text-xs text-verdict-red">{downloadError}</p>
        )}

        {/* Sprach-Hinweis: Kursinhalt ≠ UI-Sprache → Einstieg in den
            Übersetzungs-Review-Flow (TranslateBar im Tab „Veranstaltung") */}
        <ContentLangBanner
          contentLang={contentLang}
          onTranslate={() => {
            setStep(1);
            setTranslateToken((n) => n + 1);
          }}
        />

        {/* Step-Leiste wie im Wizard — direkter Sprung zu jedem Schritt,
            damit z. B. Methoden auch nach der Kurs-Anlage änderbar bleiben */}
        <StepIndicator
          labels={[
            t("wizard.steps.1"),
            t("wizard.steps.2"),
            t("wizard.steps.3"),
            t("wizard.steps.4"),
          ]}
          current={step}
          onSelect={setStep}
        />

        <div className="bg-ca-card rounded-xl border border-ca-surface-border p-8 md:p-14 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-ca-accent to-transparent opacity-30" />

          <div className="mb-8">
            <p className="text-ca-surface-muted font-medium">{STEPS[step - 1].desc}</p>
            <h2 className="text-3xl font-black text-ca-surface-ink tracking-tight uppercase mt-1">
              {STEPS[step - 1].title}
            </h2>
          </div>

          {step === 1 && (
            <section className="animate-fade-in">
              <div className="space-y-5">
                <div className="flex items-center gap-2">
                  <input
                    className="flex-1 px-6 py-4 rounded-xl bg-white border border-ca-surface-border text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent"
                    placeholder={t("editor.titlePlaceholder")}
                    value={course.title}
                    onChange={(e) => setCourse({ ...course, title: e.target.value })}
                    onBlur={() => updateCourse({ title: course.title })}
                  />
                  <AiAssistButton
                    courseId={course.id}
                    fieldKind="course_title"
                    getText={() => course.title}
                    onApply={(x) => updateCourse({ title: x })}
                    strategyHint={hintFor(null)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    className="flex-1 px-6 py-4 rounded-xl bg-white border border-ca-surface-border text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent"
                    placeholder={t("editor.audiencePlaceholder")}
                    value={course.audience || ""}
                    onChange={(e) => setCourse({ ...course, audience: e.target.value })}
                    onBlur={() => updateCourse({ audience: course.audience })}
                  />
                  <AiAssistButton
                    courseId={course.id}
                    fieldKind="audience"
                    getText={() => course.audience || ""}
                    onApply={(x) => updateCourse({ audience: x })}
                    strategyHint={hintFor(null)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-ca-surface-muted mb-2">
                    {t("editor.formatLabel")}
                  </label>
                  {catalog?.course_formats ? (
                    <CourseFormatPicker
                      value={course.format || ""}
                      onChange={(v) => updateCourse({ format: v || undefined })}
                      formats={mergeEditorFormats(catalog)}
                    />
                  ) : (
                    <input
                      className="w-full bg-white border border-ca-surface-border rounded-xl px-6 py-4 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent"
                      placeholder={t("editor.catalogLoading")}
                      value={course.format || ""}
                      onChange={(e) => updateCourse({ format: e.target.value || undefined })}
                    />
                  )}
                </div>
                <select
                  className="w-full bg-white border border-ca-surface-border rounded-xl px-6 py-4 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent"
                  value={course.modality || ""}
                  onChange={(e) =>
                    updateCourse({
                      modality: (e.target.value || undefined) as Course["modality"],
                    })
                  }
                >
                  <option value="">{t("editor.modalityPlaceholder")}</option>
                  {MODALITY_VALUES.map((v) => (
                    <option key={v} value={v}>
                      {modalityLabels[v]}
                    </option>
                  ))}
                </select>
                {contextKindLabel(course.metadata?.context_kind as string) && (
                  <p className="text-xs text-ca-surface-muted">
                    {t("editor.contextLabel")}{" "}
                    <span className="font-semibold text-ca-surface-ink">
                      {contextKindLabel(course.metadata?.context_kind as string)}
                    </span>
                  </p>
                )}
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-ca-surface-muted mb-2">
                    {t("editor.contentLangLabel")}
                  </label>
                  <select
                    className="w-full bg-white border border-ca-surface-border rounded-xl px-6 py-4 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent"
                    value={contentLang}
                    onChange={(e) => void setContentLang(e.target.value as ContentLang)}
                  >
                    <option value="de">{t("common.langDe")}</option>
                    <option value="en">{t("common.langEn")}</option>
                  </select>
                  <p className="text-xs text-ca-surface-muted mt-1 italic">
                    {t("editor.contentLangHint")}
                  </p>
                </div>
                <TranslateBar
                  courseId={course.id}
                  currentLang={contentLang}
                  runToken={translateToken}
                  onApplyItem={applyTranslateItem}
                  onAppliedAll={(target) => setContentLang(target)}
                />
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-ca-surface-muted mb-2">
                    {t("editor.roughIdeaLabel")}
                  </label>
                  <textarea
                    className="w-full px-6 py-4 rounded-xl bg-white border border-ca-surface-border text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent resize-y"
                    rows={4}
                    placeholder={t("editor.roughIdeaPlaceholder")}
                    value={(course.metadata?.rough_idea as string) || ""}
                    onChange={(e) =>
                      setCourse({
                        ...course,
                        metadata: { ...(course.metadata || {}), rough_idea: e.target.value },
                      })
                    }
                    onBlur={() =>
                      updateCourse({
                        metadata: { rough_idea: (course.metadata?.rough_idea as string) || "" },
                      })
                    }
                  />
                </div>
                <StrategyPanel
                  value={strategy}
                  onChange={updateStrategy}
                  scope="global"
                />
              </div>
              <div className="flex gap-4 mt-10">
                <button
                  onClick={() => navigate(`/courses/${course.id}`)}
                  className="flex-1 py-4 border border-ca-surface-border rounded-xl text-ca-surface-muted font-black uppercase tracking-widest hover:text-ca-surface-ink transition"
                >
                  {t("editor.toOverview")}
                </button>
                <button
                  onClick={() => setStep(2)}
                  disabled={!course.title || !course.audience}
                  className="flex-[2] py-4 bg-ca-accent text-ca-brand font-black uppercase tracking-widest rounded-xl shadow-xl hover:scale-[1.02] disabled:opacity-30 transition active:scale-95"
                >
                  {t("editor.toIlos")}
                </button>
              </div>
            </section>
          )}

          {step === 2 && (
            <section className="animate-fade-in">
              <StrategyPanel
                value={strategy}
                onChange={updateStrategy}
                scope="ilo"
                applyTo={{
                  courseId: course.id,
                  category: "ilos",
                  label: t("editor.ilosLabel"),
                  hasEntries: (course.ilos || []).length > 0,
                  onApply: (entryId, newText) => updateILO(entryId, { text: newText }),
                }}
              />
              <div className="mb-8 space-y-4">
                <AlignmentOverview rows={overviewRows} />
                {hintsPanel}
              </div>
              <div className="space-y-5 mb-8">
                {/* Anzeige strikt nach position — nach einem Hoch/Runter-
                    Tausch ändert sich die Reihenfolge sofort, auch wenn das
                    Array im State seine ursprüngliche Reihenfolge behält. */}
                {[...(course.ilos || [])]
                  .sort((a, b) => a.position - b.position)
                  .map((ilo, idx, sorted) => (
                  <ILOCard
                    key={ilo.id}
                    ilo={ilo}
                    courseId={course.id}
                    strategyHint={hintFor("ilo")}
                    onChange={(patch) => updateILO(ilo.id, patch)}
                    onDelete={() => deleteILO(ilo.id)}
                    onMoveUp={idx > 0 ? () => moveILO(ilo.id, -1) : undefined}
                    onMoveDown={idx < sorted.length - 1 ? () => moveILO(ilo.id, 1) : undefined}
                  />
                ))}
                {(course.ilos || []).length === 0 && (
                  <EmptyHint text={t("editor.ilosEmpty")} />
                )}
              </div>
              <div className="flex flex-col gap-3">
                <button
                  onClick={addILO}
                  className="w-full py-5 border-2 border-dashed border-ca-accent/40 rounded-xl text-ca-surface-accent font-black uppercase tracking-widest hover:bg-ca-accent/5 transition"
                >
                  {t("editor.addIlo")}
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 py-4 border border-ca-surface-border rounded-xl text-ca-surface-muted font-black uppercase tracking-widest hover:text-ca-surface-ink transition"
                  >
                    {t("common.back")}
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    disabled={!course.ilos || course.ilos.length === 0}
                    className="flex-[2] py-4 bg-ca-accent text-ca-brand font-black uppercase tracking-widest rounded-xl shadow-xl hover:scale-[1.02] disabled:opacity-30 transition active:scale-95"
                  >
                    {t("editor.toTlas")}
                  </button>
                </div>
              </div>
            </section>
          )}

          {step === 3 && (
            <section className="animate-fade-in">
              <StrategyPanel
                value={strategy}
                onChange={updateStrategy}
                scope="tla"
                applyTo={{
                  courseId: course.id,
                  category: "tlas",
                  label: t("editor.tlasLabel"),
                  hasEntries: (course.tlas || []).length > 0,
                  onApply: (entryId, newText) => updateTLA(entryId, { description: newText }),
                }}
              />
              <SessionBar
                courseId={course.id}
                sessions={sessions}
                activeId={sessionFilter}
                onSelect={setSessionFilter}
                onChanged={refreshSessions}
                onDeleted={handleSessionDeleted}
              />
              <div className="mb-8">{hintsPanel}</div>
              <div className="space-y-8 mb-8">
                {(() => {
                  // Session-Filter: gewählte Session + kursweite TLAs (ohne session_id)
                  const visibleTlas = (course.tlas || []).filter(
                    (x) =>
                      !sessionFilter || !x.session_id || x.session_id === sessionFilter,
                  );
                  return (
                    <>
                      {phaseOptions.map((phase) => {
                        const group = visibleTlas.filter(
                          (x) => (x.phase || "in") === phase.value,
                        );
                        if (group.length === 0) return null;
                        return (
                          <div key={phase.value} className="space-y-5">
                            <h3 className="text-xs font-black uppercase tracking-widest text-ca-surface-muted border-b border-ca-surface-border pb-2">
                              {phase.label}
                            </h3>
                            {group.map((tla) => (
                              <TLACard
                                key={tla.id}
                                tla={tla}
                                ilos={course.ilos || []}
                                catalog={catalog}
                                sessions={sessions}
                                courseId={course.id}
                                strategyHint={hintFor("tla")}
                                onChange={(patch) => updateTLA(tla.id, patch)}
                                onDelete={() => deleteTLA(tla.id)}
                              />
                            ))}
                          </div>
                        );
                      })}
                      {(course.tlas || []).length === 0 && (
                        <EmptyHint text={t("editor.tlasEmpty")} />
                      )}
                      {(course.tlas || []).length > 0 && visibleTlas.length === 0 && (
                        <EmptyHint text={t("editor.tlasEmptyFilter")} />
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="flex flex-col gap-3">
                <button
                  onClick={addTLA}
                  className="w-full py-5 border-2 border-dashed border-ca-accent/40 rounded-xl text-ca-surface-accent font-black uppercase tracking-widest hover:bg-ca-accent/5 transition"
                >
                  {t("editor.addTla")}
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(2)}
                    className="flex-1 py-4 border border-ca-surface-border rounded-xl text-ca-surface-muted font-black uppercase tracking-widest hover:text-ca-surface-ink transition"
                  >
                    {t("common.back")}
                  </button>
                  <button
                    onClick={() => setStep(4)}
                    className="flex-[2] py-4 bg-ca-accent text-ca-brand font-black uppercase tracking-widest rounded-xl shadow-xl hover:scale-[1.02] transition active:scale-95"
                  >
                    {t("editor.toAts")}
                  </button>
                </div>
              </div>
            </section>
          )}

          {step === 4 && (
            <section className="animate-fade-in">
              <StrategyPanel
                value={strategy}
                onChange={updateStrategy}
                scope="at"
                applyTo={{
                  courseId: course.id,
                  category: "ats",
                  label: t("editor.atsLabel"),
                  hasEntries: (course.ats || []).length > 0,
                  onApply: (entryId, newText) => updateAT(entryId, { description: newText }),
                }}
              />
              <div className="mb-8">{hintsPanel}</div>
              <div className="space-y-5 mb-8">
                {(course.ats || []).map((at) => (
                  <ATCard
                    key={at.id}
                    at={at}
                    ilos={course.ilos || []}
                    courseId={course.id}
                    strategyHint={hintFor("at")}
                    onChange={(patch) => updateAT(at.id, patch)}
                    onDelete={() => deleteAT(at.id)}
                  />
                ))}
                {(course.ats || []).length === 0 && (
                  <EmptyHint text={t("editor.atsEmpty")} />
                )}
              </div>

              <button
                onClick={addAT}
                className="w-full py-5 border-2 border-dashed border-ca-accent/40 rounded-xl text-ca-surface-accent font-black uppercase tracking-widest hover:bg-ca-accent/5 transition mb-8"
              >
                {t("editor.addAt")}
              </button>

              <button
                onClick={() => setAutoExportPdf((v) => !v)}
                className="w-full mb-8 p-6 bg-ca-card border border-ca-surface-border rounded-2xl flex items-center justify-between hover:bg-ca-card transition group"
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`w-7 h-7 rounded-md border-2 flex items-center justify-center transition ${
                      autoExportPdf ? "bg-ca-accent border-ca-accent" : "border-ca-surface-border"
                    }`}
                  >
                    {autoExportPdf && (
                      <span className="text-ca-brand font-black text-sm">✓</span>
                    )}
                  </div>
                  <div className="text-left">
                    <p className="font-black text-ca-surface-ink uppercase tracking-wider">
                      {t("editor.autoPdfTitle")}
                    </p>
                    <p className="text-xs text-ca-surface-muted font-bold uppercase tracking-tighter">
                      {t("editor.autoPdfBody")}
                    </p>
                  </div>
                </div>
                <span className="text-2xl opacity-30 group-hover:opacity-100 group-hover:text-ca-accent-text transition">
                  📄
                </span>
              </button>

              {feasibility && (feasibility.blockers.length > 0 || feasibility.warnings.length > 0) && (
                <div className="mb-6">
                  <FeasibilityPanel report={feasibility} onFix={(s) => setStep(s)} />
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => setStep(3)}
                  className="flex-1 py-4 border border-ca-surface-border rounded-xl text-ca-surface-muted font-black uppercase tracking-widest hover:text-ca-surface-ink transition"
                >
                  {t("common.back")}
                </button>
                <button
                  onClick={runAudit}
                  disabled={feasibility ? !feasibility.feasible : !course.ats || course.ats.length === 0}
                  className="flex-[2] py-4 bg-ca-accent text-ca-brand font-black uppercase tracking-widest rounded-xl hover:scale-[1.02] disabled:opacity-30 transition active:scale-95"
                >
                  {t("overview.auditStart")}
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}

function EmptyHint({ text }: { text: string }) {
  return (
    <div className="p-8 text-center bg-ca-card rounded-2xl border border-dashed border-ca-surface-border">
      <p className="text-ca-surface-muted italic">{text}</p>
    </div>
  );
}

// Kurs-Übersetzung (i18n): /assist/translate liefert Alt/Neu-Paare für alle
// Textfelder des Kurses — Review-Liste wie beim Strategie-Apply in der
// StrategyPanel. „Alle übernehmen" wendet der Reihe nach an und setzt danach
// content_lang auf die Zielsprache.
function TranslateBar({
  courseId,
  currentLang,
  runToken = 0,
  onApplyItem,
  onAppliedAll,
}: {
  courseId: string;
  currentLang: ContentLang;
  // Externer Start-Impuls (Banner/Deep-Link): bei jedem Token-Wechsel > 0
  // startet der Review-Flow automatisch (wie Klick auf den Übersetzen-Button).
  runToken?: number;
  onApplyItem: (item: TranslateItem) => Promise<void>;
  onAppliedAll: (target: ContentLang) => Promise<void>;
}) {
  const { t } = useTranslation();
  const target: ContentLang = currentLang === "de" ? "en" : "de";
  const [busy, setBusy] = useState(false);
  const [applyAllBusy, setApplyAllBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<unknown>(null);
  const [items, setItems] = useState<TranslateItem[] | null>(null);
  const [applyingKey, setApplyingKey] = useState<string | null>(null);

  const KIND_LABEL_KEY: Record<string, string> = {
    course: "translate.kind.course",
    ilo: "translate.kind.ilo",
    tla: "translate.kind.tla",
    at: "translate.kind.at",
    session: "translate.kind.session",
  };

  const itemKey = (item: TranslateItem) => `${item.kind}:${item.id}:${item.field}`;

  async function run() {
    setBusy(true);
    setError(null);
    setItems(null);
    try {
      const res = await api.translateCourse(courseId, target);
      setItems(res.items);
      if (res.items.length === 0) {
        setError(t("translate.none"));
      }
    } catch (e) {
      if (isQuotaError(e)) {
        setQuota(e.data);
      } else {
        setError(
          e instanceof APIError
            ? e.status === 429
              ? t("common.aiLimit")
              : e.detail
            : t("translate.failed"),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  // Externer Trigger (Banner, ?translate=1) — startet denselben Flow wie der Button.
  useEffect(() => {
    if (!runToken) return;
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runToken]);

  function dismiss(key: string) {
    setItems((prev) => (prev ? prev.filter((i) => itemKey(i) !== key) : prev));
  }

  async function applyOne(item: TranslateItem) {
    setApplyingKey(itemKey(item));
    setError(null);
    try {
      await onApplyItem(item);
      dismiss(itemKey(item));
    } catch (e) {
      setError(e instanceof APIError ? e.detail : t("common.applyFailed"));
    } finally {
      setApplyingKey(null);
    }
  }

  async function applyAll() {
    if (!items) return;
    setApplyAllBusy(true);
    setError(null);
    try {
      for (const item of items) {
        // sequenziell, damit die PATCHes nicht überholen
        // eslint-disable-next-line no-await-in-loop
        await onApplyItem(item);
      }
      setItems(null);
      await onAppliedAll(target);
    } catch (e) {
      setError(e instanceof APIError ? e.detail : t("common.applyFailed"));
    } finally {
      setApplyAllBusy(false);
    }
  }

  return (
    <div className="p-4 bg-ca-card rounded-2xl border border-ca-surface-border">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={run}
          disabled={busy || applyAllBusy}
          title={t("translate.buttonTitle")}
          className="px-4 py-2 rounded-md bg-ca-accent text-ca-brand text-xs font-black uppercase tracking-widest disabled:opacity-40 transition"
        >
          {busy
            ? t("translate.running")
            : target === "en"
              ? t("translate.toEn")
              : t("translate.toDe")}
        </button>
        {items && items.length > 0 && (
          <button
            type="button"
            onClick={applyAll}
            disabled={applyAllBusy || applyingKey !== null}
            className="px-4 py-2 rounded-md bg-ca-accent text-ca-brand text-xs font-black uppercase tracking-widest disabled:opacity-40 transition"
          >
            {applyAllBusy ? t("common.saving") : t("translate.applyAll")}
          </button>
        )}
      </div>
      {quota !== null && (
        <div className="mt-2">
          <QuotaExhaustedCard detail={quota} onDismiss={() => setQuota(null)} />
        </div>
      )}
      {error && <p className="mt-2 text-xs text-verdict-red">{error}</p>}

      {items && items.length > 0 && (
        <div className="mt-4 space-y-3">
          <p className="text-[10px] font-black uppercase tracking-widest text-ca-surface-muted">
            {t("translate.reviewHint")}
          </p>
          {items.map((item) => (
            <div key={itemKey(item)} className="p-3 rounded-xl border border-ca-surface-border bg-ca-card">
              <p className="text-[10px] font-black uppercase tracking-widest text-ca-surface-muted mb-1">
                {t(KIND_LABEL_KEY[item.kind] ?? "translate.kind.other")}
                {item.kind === "course" && ` · ${t(item.field === "title" ? "translate.field.title" : "translate.field.audience")}`}
              </p>
              <p className="text-xs text-ca-surface-muted whitespace-pre-wrap">
                <span className="font-bold">{t("common.before")}: </span>
                {item.old_text || t("common.empty")}
              </p>
              <p className="mt-2 text-sm text-ca-surface-ink whitespace-pre-wrap">
                <span className="font-bold">{t("common.new")}: </span>
                {item.new_text}
              </p>
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => applyOne(item)}
                  disabled={applyingKey !== null || applyAllBusy}
                  className="px-3 py-1 rounded-md bg-ca-accent text-ca-brand text-[11px] font-black uppercase tracking-widest disabled:opacity-40 transition"
                >
                  {applyingKey === itemKey(item) ? t("common.saving") : t("common.apply")}
                </button>
                <button
                  type="button"
                  onClick={() => dismiss(itemKey(item))}
                  className="px-3 py-1 rounded-md border border-ca-surface-border text-ca-surface-muted text-[11px] font-semibold hover:text-ca-surface-ink transition"
                >
                  {t("common.discard")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Session-Leiste (F4): Chips je Session wirken als Filter auf die TLA-Liste,
// dazu Anlegen einzelner Sessions und deterministischer Serien (z. B. „alle 2
// Wochen ein Präsenztag") über /sessions/series.
function SessionBar({
  courseId,
  sessions,
  activeId,
  onSelect,
  onChanged,
  onDeleted,
}: {
  courseId: string;
  sessions: CourseSession[];
  activeId: string | null;
  onSelect: (id: string | null) => void;
  onChanged: () => Promise<void>;
  onDeleted: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const sessionKindLabels = useSessionKindLabels();
  const [mode, setMode] = useState<"none" | "new" | "edit" | "series">("none");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Formular: einzelne Session („new" = anlegen, „edit" = bestehende bearbeiten,
  // editingId zeigt dann auf die Session in Arbeit)
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState<SessionKind>("presence_day");
  const [date, setDate] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [modality, setModality] = useState("");

  // Formular: Serie
  const [sTitle, setSTitle] = useState("");
  const [sKind, setSKind] = useState<SessionKind>("presence_day");
  const [sCount, setSCount] = useState(4);
  const [sInterval, setSInterval] = useState(1);
  const [sDate, setSDate] = useState("");
  const [sStart, setSStart] = useState("");
  const [sEnd, setSEnd] = useState("");
  const [sModality, setSModality] = useState("");

  const inputCls =
    "w-full px-3 py-2 rounded-md bg-white border border-ca-surface-border text-ca-surface-ink placeholder:text-ca-surface-muted text-sm outline-none focus:border-ca-accent";
  const labelCls =
    "block text-[10px] font-black uppercase tracking-widest text-ca-surface-muted mb-1";

  const submitNew = async () => {
    if (!title.trim()) {
      setError(t("editor.sessionTitleMissing"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createSession(courseId, {
        title: title.trim(),
        kind,
        date: date || null,
        start: start || null,
        end: end || null,
        modality: modality.trim() || null,
      });
      resetSingleForm();
      setMode("none");
      await onChanged();
    } catch (e) {
      setError(e instanceof APIError ? e.detail : String(e));
    } finally {
      setBusy(false);
    }
  };

  const resetSingleForm = () => {
    setEditingId(null);
    setTitle("");
    setDate("");
    setStart("");
    setEnd("");
    setModality("");
  };

  // Bestehende Session in dasselbe Formular laden (wie „+ Session", vorbefüllt)
  const startEdit = (s: CourseSession) => {
    setEditingId(s.id);
    setTitle(s.title);
    setKind(s.kind);
    setDate(s.date || "");
    setStart(s.start || "");
    setEnd(s.end || "");
    setModality(s.modality || "");
    setError(null);
    setMode("edit");
  };

  const submitEdit = async () => {
    if (!editingId) return;
    if (!title.trim()) {
      setError(t("editor.sessionTitleMissing"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.updateSession(courseId, editingId, {
        title: title.trim(),
        kind,
        date: date || null,
        start: start || null,
        end: end || null,
        modality: modality.trim() || null,
      });
      resetSingleForm();
      setMode("none");
      await onChanged();
    } catch (e) {
      setError(e instanceof APIError ? e.detail : String(e));
    } finally {
      setBusy(false);
    }
  };

  const submitSeries = async () => {
    if (!sTitle.trim()) {
      setError(t("editor.sessionTitleMissing"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createSessionSeries(courseId, {
        title: sTitle.trim(),
        kind: sKind,
        count: sCount,
        interval_weeks: sInterval,
        start_date: sDate || null,
        day_start: sStart || null,
        day_end: sEnd || null,
        modality: sModality.trim() || null,
      });
      setSTitle("");
      setSDate("");
      setSStart("");
      setSEnd("");
      setSModality("");
      setMode("none");
      await onChanged();
    } catch (e) {
      setError(e instanceof APIError ? e.detail : String(e));
    } finally {
      setBusy(false);
    }
  };

  const removeSession = async (s: CourseSession) => {
    if (!window.confirm(t("editor.sessionDeleteConfirm", { title: s.title }))) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.deleteSession(courseId, s.id);
      await onDeleted();
    } catch (e) {
      setError(e instanceof APIError ? e.detail : String(e));
    } finally {
      setBusy(false);
    }
  };

  const chipCls = (active: boolean) =>
    `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition ${
      active
        ? "border-ca-accent bg-ca-accent text-ca-brand"
        : "border-ca-surface-border bg-ca-card text-ca-surface-ink hover:border-ca-accent"
    }`;

  return (
    <div className="mb-8 p-6 bg-ca-card rounded-2xl border border-ca-surface-border">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="text-xs font-black uppercase tracking-widest text-ca-surface-muted">
          {t("editor.sessionsTitle")}
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => {
              resetSingleForm();
              setMode(mode === "new" ? "none" : "new");
            }}
            className="px-3 py-1.5 rounded-md border border-ca-surface-border bg-ca-card text-ca-surface-ink text-xs font-bold hover:border-ca-accent transition"
          >
            {t("editor.sessionAdd")}
          </button>
          <button
            onClick={() => setMode(mode === "series" ? "none" : "series")}
            className="px-3 py-1.5 rounded-md border border-ca-surface-border bg-ca-card text-ca-surface-ink text-xs font-bold hover:border-ca-accent transition"
            title={t("editor.sessionSeriesTitle")}
          >
            {t("editor.sessionSeriesAdd")}
          </button>
        </div>
      </div>

      {sessions.length === 0 && mode === "none" && (
        <p className="text-xs text-ca-surface-muted italic">
          {t("editor.sessionsEmpty")}
        </p>
      )}

      {sessions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => onSelect(null)} className={chipCls(activeId === null)}>
            {t("editor.sessionsAll")}
          </button>
          {sessions.map((s) => (
            <span key={s.id} className={chipCls(activeId === s.id)}>
              <button
                onClick={() => onSelect(activeId === s.id ? null : s.id)}
                title={t("editor.sessionFilterTitle")}
              >
                {s.title}
                {formatSessionDate(s.date, dateLocale) && ` · ${formatSessionDate(s.date, dateLocale)}`}
                {` · ${sessionKindLabels[s.kind] || s.kind}`}
              </button>
              <button
                onClick={() => startEdit(s)}
                disabled={busy}
                title={t("editor.sessionEditTitle")}
                className="opacity-50 hover:opacity-100 transition"
              >
                ✎
              </button>
              <button
                onClick={() => removeSession(s)}
                disabled={busy}
                title={t("editor.sessionDeleteTitle")}
                className="opacity-50 hover:opacity-100 transition"
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-verdict-red mt-3">{error}</p>}

      {(mode === "new" || mode === "edit") && (
        <div className="mt-4 pt-4 border-t border-ca-surface-border">
          {mode === "edit" && (
            <p className="text-[10px] font-black uppercase tracking-widest text-ca-surface-muted mb-3">
              {t("editor.sessionEditTitle")}
            </p>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="col-span-2 md:col-span-1">
              <label className={labelCls}>{t("editor.sessionTitle")}</label>
              <input
                className={inputCls}
                placeholder={t("editor.sessionTitlePlaceholder")}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>{t("editor.sessionKind")}</label>
              <select
                className={inputCls}
                value={kind}
                onChange={(e) => setKind(e.target.value as SessionKind)}
              >
                {(Object.keys(sessionKindLabels) as SessionKind[]).map((value) => (
                  <option key={value} value={value}>
                    {sessionKindLabels[value]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>{t("editor.sessionDate")}</label>
              <input
                type="date"
                className={inputCls}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>{t("editor.sessionStart")}</label>
              <input
                type="time"
                className={inputCls}
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>{t("editor.sessionEnd")}</label>
              <input
                type="time"
                className={inputCls}
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>{t("editor.sessionModality")}</label>
              <input
                className={inputCls}
                placeholder={t("editor.sessionModalityPlaceholder")}
                value={modality}
                onChange={(e) => setModality(e.target.value)}
              />
            </div>
          </div>
          <button
            onClick={mode === "edit" ? submitEdit : submitNew}
            disabled={busy}
            className="mt-3 px-4 py-2 rounded-md bg-ca-accent text-ca-brand text-xs font-black uppercase tracking-widest disabled:opacity-40 transition"
          >
            {busy
              ? t("common.saving")
              : mode === "edit"
                ? t("editor.sessionSave")
                : t("editor.sessionCreate")}
          </button>
        </div>
      )}

      {mode === "series" && (
        <div className="mt-4 pt-4 border-t border-ca-surface-border">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="col-span-2 md:col-span-1">
              <label className={labelCls}>{t("editor.sessionTitle")}</label>
              <input
                className={inputCls}
                placeholder={t("editor.seriesTitlePlaceholder")}
                value={sTitle}
                onChange={(e) => setSTitle(e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>{t("editor.sessionKind")}</label>
              <select
                className={inputCls}
                value={sKind}
                onChange={(e) => setSKind(e.target.value as SessionKind)}
              >
                {(Object.keys(sessionKindLabels) as SessionKind[]).map((value) => (
                  <option key={value} value={value}>
                    {sessionKindLabels[value]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>{t("editor.seriesCount")}</label>
              <input
                type="number"
                min={1}
                max={52}
                className={inputCls}
                value={sCount}
                onChange={(e) => setSCount(parseInt(e.target.value || "1", 10))}
              />
            </div>
            <div>
              <label className={labelCls}>{t("editor.seriesInterval")}</label>
              <input
                type="number"
                min={0}
                className={inputCls}
                value={sInterval}
                onChange={(e) => setSInterval(parseInt(e.target.value || "0", 10))}
              />
            </div>
            <div>
              <label className={labelCls}>{t("editor.seriesStartDate")}</label>
              <input
                type="date"
                className={inputCls}
                value={sDate}
                onChange={(e) => setSDate(e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>{t("editor.seriesStartEnd")}</label>
              <div className="flex gap-2">
                <input
                  type="time"
                  className={inputCls}
                  value={sStart}
                  onChange={(e) => setSStart(e.target.value)}
                />
                <input
                  type="time"
                  className={inputCls}
                  value={sEnd}
                  onChange={(e) => setSEnd(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>{t("editor.sessionModality")}</label>
              <input
                className={inputCls}
                placeholder={t("editor.sessionModalityPlaceholder")}
                value={sModality}
                onChange={(e) => setSModality(e.target.value)}
              />
            </div>
          </div>
          <button
            onClick={submitSeries}
            disabled={busy}
            className="mt-3 px-4 py-2 rounded-md bg-ca-accent text-ca-brand text-xs font-black uppercase tracking-widest disabled:opacity-40 transition"
          >
            {busy ? t("editor.seriesCreating") : t("editor.seriesCreate", { count: sCount })}
          </button>
        </div>
      )}
    </div>
  );
}

function ILOCard({
  ilo,
  courseId,
  strategyHint,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
}: {
  ilo: ILO;
  courseId: string;
  strategyHint?: string;
  onChange: (patch: Partial<ILO>) => Promise<void>;
  onDelete: () => Promise<void>;
  onMoveUp?: () => Promise<void>;
  onMoveDown?: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [verb, setVerb] = useState(ilo.action_verb || "");
  const [text, setText] = useState(ilo.text || "");
  const [bender, setBender] = useState(ilo.bender_depth || "");

  useDebouncedSave(verb, (v) => onChange({ action_verb: v }));
  useDebouncedSave(text, (v) => onChange({ text: v }));
  useDebouncedSave(bender, (v) =>
    onChange({ bender_depth: (v || undefined) as ILO["bender_depth"] }),
  );

  return (
    <div className="p-8 bg-ca-card rounded-3xl border border-ca-surface-border relative">
      <div className="absolute top-4 right-4 flex items-center gap-1">
        {/* Reihenfolge per Hoch/Runter (bewusst kein Drag&Drop) — die
            Nachbar-ILOs tauschen ihre positions (moveILO im Editor). */}
        <button
          type="button"
          onClick={onMoveUp}
          disabled={!onMoveUp}
          aria-label={t("editor.iloMoveUp", { n: ilo.position + 1 })}
          title={t("editor.iloMoveUp", { n: ilo.position + 1 })}
          className="px-1.5 text-ca-surface-muted hover:text-ca-surface-accent transition disabled:opacity-30 disabled:cursor-not-allowed"
        >
          ↑
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={!onMoveDown}
          aria-label={t("editor.iloMoveDown", { n: ilo.position + 1 })}
          title={t("editor.iloMoveDown", { n: ilo.position + 1 })}
          className="px-1.5 text-ca-surface-muted hover:text-ca-surface-accent transition disabled:opacity-30 disabled:cursor-not-allowed"
        >
          ↓
        </button>
        <button
          onClick={onDelete}
          className="px-1.5 text-ca-surface-muted hover:text-verdict-red transition"
          title={t("common.delete")}
        >
          ✕
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-6 mb-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest">
              {t("editor.iloVerbLabel")}
            </label>
            <AiAssistButton
              courseId={courseId}
              fieldKind="ilo_verb"
              getText={() => verb}
              onApply={setVerb}
              strategyHint={strategyHint}
            />
          </div>
          <input
            className="w-full bg-transparent border-b border-ca-surface-border py-2 font-black text-xl text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent"
            placeholder={t("editor.iloVerbPlaceholder")}
            value={verb}
            onChange={(e) => setVerb(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
            {t("editor.iloDepthLabel")}
          </label>
          <select
            className="w-full bg-transparent border-b border-ca-surface-border py-2 text-ca-surface-ink outline-none focus:border-ca-accent"
            value={bender}
            onChange={(e) => setBender(e.target.value)}
          >
            <option value="">{t("editor.iloDepthOpen")}</option>
            {BENDER_OPTIONS.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest">
          {t("editor.iloTextLabel")}
        </label>
        <AiAssistButton
          courseId={courseId}
          fieldKind="ilo_text"
          getText={() => text}
          onApply={setText}
          strategyHint={strategyHint}
        />
      </div>
      <textarea
        className="w-full bg-white border border-ca-surface-border rounded-xl p-4 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent"
        placeholder={t("editor.iloTextPlaceholder")}
        rows={2}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
    </div>
  );
}

function TLACard({
  tla,
  ilos,
  catalog,
  sessions,
  courseId,
  strategyHint,
  onChange,
  onDelete,
}: {
  tla: TLA;
  ilos: ILO[];
  catalog: WizardCatalog | null;
  sessions: CourseSession[];
  courseId: string;
  strategyHint?: string;
  onChange: (patch: Partial<TLA>) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const phaseOptions = usePhaseOptions();
  const [type, setType] = useState(tla.type || "");
  const [duration, setDuration] = useState(tla.duration_minutes ?? 60);
  const [desc, setDesc] = useState(tla.description || "");

  useDebouncedSave(type, (v) => onChange({ type: v }));
  useDebouncedSave(duration, (v) => onChange({ duration_minutes: v }));
  useDebouncedSave(desc, (v) => onChange({ description: v }));

  const tlaTypes = catalog?.tla_types ?? [];
  const knownTlaIds = new Set(tlaTypes.map((o) => o.id));
  const tlaIsCustom = type && !knownTlaIds.has(type);

  return (
    <div className="p-8 bg-ca-card rounded-3xl border border-ca-surface-border relative">
      <button
        onClick={onDelete}
        title={t("common.delete")}
        className="absolute top-4 right-4 text-ca-surface-muted hover:text-verdict-red transition"
      >
        ✕
      </button>
      <div className="grid md:grid-cols-2 gap-6 mb-4">
        <div>
          <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
            {t("editor.tlaMethodLabel")}
          </label>
          <select
            className="w-full bg-transparent border-b border-ca-surface-border py-2 text-ca-surface-ink font-black outline-none focus:border-ca-accent"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="">—</option>
            {tlaTypes.map((o) => (
              <option key={o.id} value={o.id} title={o.description ?? undefined}>
                {o.name}
              </option>
            ))}
            {tlaIsCustom && (
              <option value={type}>{type} {t("common.customLabel")}</option>
            )}
          </select>
        </div>
        <div>
          <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
            {t("editor.durationLabel")}
          </label>
          <input
            type="number"
            className="w-full bg-transparent border-b border-ca-surface-border py-2 text-ca-surface-ink outline-none focus:border-ca-accent"
            value={duration}
            min={0}
            onChange={(e) => setDuration(parseInt(e.target.value || "0", 10))}
          />
        </div>
      </div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest">
          {t("editor.descriptionLabel")}
        </label>
        <AiAssistButton
          courseId={courseId}
          fieldKind="tla_description"
          getText={() => desc}
          onApply={setDesc}
          strategyHint={strategyHint}
        />
      </div>
      <textarea
        className="w-full bg-white border border-ca-surface-border rounded-xl p-4 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent mb-4"
        placeholder={t("editor.tlaDescPlaceholder")}
        rows={2}
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
      />
      <div className="grid md:grid-cols-2 gap-6 mb-4">
        <div>
          <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
            {t("editor.tlaPhaseLabel")}
          </label>
          <select
            className="w-full bg-transparent border-b border-ca-surface-border py-2 text-ca-surface-ink outline-none focus:border-ca-accent"
            value={tla.phase || "in"}
            onChange={(e) => onChange({ phase: e.target.value as TLAPhase })}
          >
            {phaseOptions.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
            {t("editor.tlaSessionLabel")}
          </label>
          <select
            className="w-full bg-transparent border-b border-ca-surface-border py-2 text-ca-surface-ink outline-none focus:border-ca-accent"
            value={tla.session_id || ""}
            onChange={(e) => onChange({ session_id: e.target.value || null })}
          >
            <option value="">{t("editor.tlaSessionNone")}</option>
            {sessions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
                {formatSessionDate(s.date, dateLocale) ? ` (${formatSessionDate(s.date, dateLocale)})` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>
      <LinkedILOsEditor
        label={t("editor.tlaPreparesFor")}
        ilos={[...ilos]
          .sort((a, b) => a.position - b.position)
          .map((ilo) => ({
            key: ilo.id,
            label: `LZ ${ilo.position + 1}`,
            title: ilo.text || ilo.action_verb || "",
          }))}
        selected={tla.linked_ilo_ids}
        onToggle={(iloId) =>
          void onChange({
            linked_ilo_ids: tla.linked_ilo_ids.includes(iloId)
              ? tla.linked_ilo_ids.filter((x) => x !== iloId)
              : [...tla.linked_ilo_ids, iloId],
          })
        }
        emptyHint={t("editor.autoAssign")}
      />
    </div>
  );
}

function ATCard({
  at,
  ilos,
  courseId,
  strategyHint,
  onChange,
  onDelete,
}: {
  at: AT;
  ilos: ILO[];
  courseId: string;
  strategyHint?: string;
  onChange: (patch: Partial<AT>) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const atFormatLabels = useAtFormatLabels();
  const [type, setType] = useState(at.type || "");
  const [format, setFormat] = useState(at.format || "");
  const [desc, setDesc] = useState(at.description || "");
  const [duration, setDuration] = useState(at.duration_minutes ?? 60);

  useDebouncedSave(type, (v) => onChange({ type: v }));
  useDebouncedSave(format, (v) =>
    onChange({ format: (v || undefined) as AT["format"] }),
  );
  useDebouncedSave(desc, (v) => onChange({ description: v }));
  useDebouncedSave(duration, (v) => onChange({ duration_minutes: v }));

  return (
    <div className="p-8 bg-ca-card rounded-3xl border border-ca-surface-border relative">
      <button
        onClick={onDelete}
        title={t("common.delete")}
        className="absolute top-4 right-4 text-ca-surface-muted hover:text-verdict-red transition"
      >
        ✕
      </button>
      <div className="grid md:grid-cols-3 gap-6 mb-4">
        <div>
          <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
            {t("editor.atTypeLabel")}
          </label>
          <select
            className="w-full bg-transparent border-b border-ca-surface-border py-2 text-ca-surface-ink font-black outline-none focus:border-ca-accent"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="">—</option>
            {AT_TYPE_VALUES.map((v) => (
              <option key={v} value={v}>
                {t(AT_TYPE_LABEL_KEY[v])}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
            {t("editor.atFormatLabel")}
          </label>
          <select
            className="w-full bg-transparent border-b border-ca-surface-border py-2 text-ca-surface-ink outline-none focus:border-ca-accent"
            value={format}
            onChange={(e) => setFormat(e.target.value)}
          >
            <option value="">—</option>
            <option value="closed">{atFormatLabels.closed}</option>
            <option value="semi_open">{atFormatLabels.semi_open}</option>
            <option value="open">{atFormatLabels.open}</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
            {t("editor.durationLabel")}
          </label>
          <input
            type="number"
            className="w-full bg-transparent border-b border-ca-surface-border py-2 text-ca-surface-ink outline-none focus:border-ca-accent"
            value={duration}
            min={0}
            onChange={(e) => setDuration(parseInt(e.target.value || "0", 10))}
          />
        </div>
      </div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest">
          {t("editor.descriptionLabel")}
        </label>
        <AiAssistButton
          courseId={courseId}
          fieldKind="at_description"
          getText={() => desc}
          onApply={setDesc}
          strategyHint={strategyHint}
        />
      </div>
      <textarea
        className="w-full bg-white border border-ca-surface-border rounded-xl p-4 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent mb-4"
        placeholder={t("editor.atDescPlaceholder")}
        rows={2}
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
      />
      <LinkedILOsEditor
        label={t("editor.atAssesses")}
        ilos={[...ilos]
          .sort((a, b) => a.position - b.position)
          .map((ilo) => ({
            key: ilo.id,
            label: `LZ ${ilo.position + 1}`,
            title: ilo.text || ilo.action_verb || "",
          }))}
        selected={at.linked_ilo_ids}
        onToggle={(iloId) =>
          void onChange({
            linked_ilo_ids: at.linked_ilo_ids.includes(iloId)
              ? at.linked_ilo_ids.filter((x) => x !== iloId)
              : [...at.linked_ilo_ids, iloId],
          })
        }
        emptyHint={t("editor.autoAssign")}
      />
    </div>
  );
}
